import { useState } from 'react'
import {
  Box, Container, Typography, Card, CardContent,
  TextField, Button, Grid, Chip, MenuItem,
  Select, FormControl, InputLabel, LinearProgress,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import LockIcon from '@mui/icons-material/Lock'
import FavoriteIcon from '@mui/icons-material/Favorite'
import WorkIcon from '@mui/icons-material/Work'
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import PsychologyIcon from '@mui/icons-material/Psychology'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'

const signs = [
  'Aries ♈', 'Taurus ♉', 'Gemini ♊', 'Cancer ♋',
  'Leo ♌', 'Virgo ♍', 'Libra ♎', 'Scorpio ♏',
  'Sagittarius ♐', 'Capricorn ♑', 'Aquarius ♒', 'Pisces ♓',
]

const readingCards = [
  {
    icon: <PsychologyIcon sx={{ color: '#8B7FFF' }} />,
    bg: '#1A1530', title: 'Personality',
    text: 'Bold, passionate and a natural leader. Your Aries sun gives you unstoppable drive. You act first, think later — both strength and weakness.',
  },
  {
    icon: <FavoriteIcon sx={{ color: '#D4537E' }} />,
    bg: '#1A1225', title: 'Love & relationships',
    text: 'Venus in Taurus brings deep loyalty. You love intensely but take time to open up. A significant romantic development is indicated after June.',
  },
  {
    icon: <WorkIcon sx={{ color: '#639922' }} />,
    bg: '#121A14', title: 'Career & finance',
    text: 'Saturn in your 10th house indicates career growth through hard work. A promotion or opportunity is indicated between August and October.',
  },
  {
    icon: <FitnessCenterIcon sx={{ color: '#EF9F27' }} />,
    bg: '#1A160D', title: 'Health',
    text: 'Mars rules your chart — watch for blood pressure. Regular exercise is your best remedy. Avoid late nights this month.',
  },
]

export default function Reading() {
  const [form, setForm] = useState({
    name: '', dob: '', tob: '', place: '', sign: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = () => {
    if (!form.name || !form.dob || !form.sign) return
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      setSubmitted(true)
    }, 2000)
  }

  if (submitted) return (
    <Container maxWidth="md" sx={{ py: 4 }}>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '50%',
            bgcolor: '#1A1530', border: '1px solid #3D3580',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 22, flexShrink: 0,
          }}>♈</Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={500} sx={{ color: '#E8E6FF' }}>
              {form.name}
              <Chip label="AI reading" size="small" sx={{
                ml: 1, fontSize: 10, height: 18,
                bgcolor: '#121A14', color: '#4CAF50',
                border: '1px solid #1E3A1E',
              }} />
            </Typography>
            <Typography variant="body2" sx={{ color: '#8B85B8' }}>
              Sun: {form.sign} · Born {form.dob} · {form.place}
            </Typography>
          </Box>
          <Button size="small" variant="outlined"
            onClick={() => setSubmitted(false)}
            sx={{ fontSize: 11, px: 1.5, py: 0.5 }}>
            New reading
          </Button>
        </CardContent>
      </Card>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          ['Lucky number', '7'],
          ['Lucky day', 'Tuesday'],
          ['Lucky colour', 'Red'],
          ['Gemstone', 'Ruby'],
        ].map(([k, v]) => (
          <Grid item xs={6} sm={3} key={k}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption"
                  sx={{ color: '#8B85B8', display: 'block' }}>
                  {k}
                </Typography>
                <Typography fontWeight={500}
                  sx={{ color: '#A89EFF', fontSize: 14 }}>
                  {v}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {readingCards.map(c => (
          <Grid item xs={12} sm={6} key={c.title}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{
                  width: 34, height: 34, borderRadius: 2,
                  bgcolor: c.bg, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  mb: 1.5,
                }}>{c.icon}</Box>
                <Typography fontWeight={500} gutterBottom
                  sx={{ color: '#E8E6FF', fontSize: 13 }}>
                  {c.title}
                </Typography>
                <Typography variant="body2"
                  sx={{ color: '#8B85B8', lineHeight: 1.6 }}>
                  {c.text}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography fontWeight={500} gutterBottom
            sx={{ display: 'flex', alignItems: 'center',
              gap: 1, color: '#E8E6FF', fontSize: 13 }}>
            <CalendarMonthIcon sx={{ color: '#8B7FFF', fontSize: 18 }} />
            Today's prediction
          </Typography>
          <Typography variant="body2"
            sx={{ color: '#8B85B8', lineHeight: 1.7 }}>
            The Moon in your 7th house today brings harmony in
            relationships. An old friend may reach out unexpectedly —
            this connection could be significant. Avoid major decisions
            after 6 PM. Your lucky time today is 10 AM to 12 PM.
            Wear red or orange for positive energy.
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ position: 'relative', mb: 2 }}>
        <Card>
          <CardContent sx={{
            filter: 'blur(4px)', userSelect: 'none',
          }}>
            <Typography fontWeight={500} gutterBottom
              sx={{ color: '#E8E6FF' }}>
              Full Kundali — Raj Yoga analysis
            </Typography>
            <Typography variant="body2" sx={{ color: '#8B85B8' }}>
              Your Kundali shows a rare Raj Yoga forming in your 9th
              house. Jupiter transit will bring wealth and recognition.
              Marriage indicated before age 30. Chant Hanuman Chalisa
              every Tuesday, wear 7 mukhi rudraksha for protection.
            </Typography>
          </CardContent>
        </Card>
        <Box sx={{
          position: 'absolute', inset: 0,
          bgcolor: 'rgba(13,11,26,0.88)',
          borderRadius: '12px',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column', gap: 1.5,
          textAlign: 'center', p: 3,
        }}>
          <LockIcon sx={{ color: '#8B7FFF', fontSize: 28 }} />
          <Typography fontWeight={500} sx={{ color: '#A89EFF' }}>
            Full Kundali locked
          </Typography>
          <Typography variant="body2" sx={{ color: '#8B85B8' }}>
            Unlock Raj Yoga analysis, marriage timing + PDF report
          </Typography>
          <Button variant="contained"
            href="/pricing" sx={{ borderRadius: 5, mt: 0.5 }}>
            Unlock for ₹99/month
          </Button>
        </Box>
      </Box>

    </Container>
  )

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 0.5,
          }}>
            <AutoAwesomeIcon sx={{ color: '#8B7FFF' }} />
            <Typography variant="h6" sx={{ color: '#E8E6FF' }}>
              Enter your birth details
            </Typography>
          </Box>
          <Typography variant="body2"
            sx={{ color: '#8B85B8', mb: 3 }}>
            We use Vedic astrology to calculate your exact birth chart
          </Typography>

          {loading && (
            <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

  <TextField fullWidth label="Your full name"
    value={form.name}
    onChange={e => setForm({ ...form, name: e.target.value })} />

  <TextField fullWidth label="Date of birth" type="date"
  InputLabelProps={{ shrink: true, style: { color: '#8B85B8' } }}
  InputProps={{ style: { paddingTop: '8px' } }}
  value={form.dob}
  onChange={e => setForm({ ...form, dob: e.target.value })}
  sx={{
    '& .MuiInputLabel-root': {
      transform: 'translate(14px, -9px) scale(0.75)',
      background: '#120F20',
      px: 0.5,
    },
    '& input': { paddingTop: '16px', paddingBottom: '16px' }
  }} />

<TextField fullWidth label="Time of birth" type="time"
  InputLabelProps={{ shrink: true, style: { color: '#8B85B8' } }}
  value={form.tob}
  onChange={e => setForm({ ...form, tob: e.target.value })}
  sx={{
    '& .MuiInputLabel-root': {
      transform: 'translate(14px, -9px) scale(0.75)',
      background: '#120F20',
      px: 0.5,
    },
    '& input': { paddingTop: '16px', paddingBottom: '16px' }
  }} />

  <TextField fullWidth label="Place of birth"
    placeholder="e.g. Mumbai, Maharashtra"
    value={form.place}
    onChange={e => setForm({ ...form, place: e.target.value })} />

  <FormControl fullWidth>
    <InputLabel>Sun sign</InputLabel>
    <Select value={form.sign} label="Sun sign"
      onChange={e => setForm({ ...form, sign: e.target.value })}>
      {signs.map(s => (
        <MenuItem key={s} value={s}>{s}</MenuItem>
      ))}
    </Select>
  </FormControl>

  <Button fullWidth variant="contained" size="large"
    onClick={handleSubmit} disabled={loading}
    startIcon={<AutoAwesomeIcon />}
    sx={{ py: 1.5, borderRadius: 2, fontSize: 14 }}>
    {loading
      ? 'Generating your Kundali...'
      : 'Generate my Kundali reading'}
  </Button>

</Box>
        </CardContent>
      </Card>
    </Container>
  )
}